/* Enhances the complete server-rendered product.description without truncating its HTML. */
(() => {
  if (customElements.get('db-expandable-description')) return;

  class DBExpandableDescription extends HTMLElement {
    constructor() {
      super();
      this.onToggle = this.onToggle.bind(this);
      this.onFocusInside = this.onFocusInside.bind(this);
      this.measure = this.measure.bind(this);
      this.onTransitionEnd = this.onTransitionEnd.bind(this);
    }

    connectedCallback() {
      this.body = this.querySelector('[data-db-description-body]');
      this.inner = this.querySelector('[data-db-description-inner]');
      this.button = this.querySelector('[data-db-description-toggle]');
      if (!this.body || !this.inner || !this.button) return;

      this.dataset.expanded = 'false';
      this.button.addEventListener('click', this.onToggle);
      this.body.addEventListener('focusin', this.onFocusInside);
      this.body.addEventListener('transitionend', this.onTransitionEnd);
      this.breakpoint = window.matchMedia('(min-width: 1000px)');
      this.breakpoint.addEventListener('change', this.measure);

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.measure);
        this.observer.observe(this.inner);
      } else {
        window.addEventListener('resize', this.measure);
      }

      requestAnimationFrame(this.measure);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          if (this.isConnected) this.measure();
        });
      }
    }

    disconnectedCallback() {
      if (!this.body) return;
      this.button.removeEventListener('click', this.onToggle);
      this.body.removeEventListener('focusin', this.onFocusInside);
      this.body.removeEventListener('transitionend', this.onTransitionEnd);
      this.breakpoint?.removeEventListener('change', this.measure);
      this.observer?.disconnect();
      window.removeEventListener('resize', this.measure);
    }

    get limit() {
      const attribute = this.breakpoint?.matches ? 'desktopHeight' : 'mobileHeight';
      return Math.max(0, Number(this.dataset[attribute]) || 0);
    }

    measure() {
      if (!this.isConnected || !this.inner) return;
      const isLong = this.inner.scrollHeight > this.limit + 3;
      this.dataset.collapsible = String(isLong);
      this.button.hidden = !isLong;

      if (!isLong) {
        this.dataset.expanded = 'false';
        this.button.setAttribute('aria-expanded', 'false');
        this.body.style.height = '';
      } else if (this.dataset.expanded === 'true') {
        this.body.style.height = 'auto';
      } else {
        this.body.style.height = this.limit + 'px';
      }
    }

    onToggle() {
      if (this.dataset.collapsible !== 'true') return;
      this.setExpanded(this.dataset.expanded !== 'true');
    }

    onFocusInside(event) {
      // A link hidden below the fold must never receive invisible keyboard focus.
      if (this.dataset.collapsible !== 'true' || this.dataset.expanded === 'true') return;
      const elementTop = event.target.getBoundingClientRect().top;
      const visibleBottom = this.body.getBoundingClientRect().bottom;
      if (elementTop >= visibleBottom - 4) this.setExpanded(true);
    }

    setExpanded(expanded) {
      const from = this.body.getBoundingClientRect().height;
      this.body.style.height = from + 'px';
      this.body.offsetHeight; // Commit the initial height before transitioning.
      this.dataset.expanded = String(expanded);
      this.button.setAttribute('aria-expanded', String(expanded));
      this.body.style.height = (expanded ? this.inner.scrollHeight : this.limit) + 'px';
      if (expanded && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.body.style.height = 'auto';
      }
    }

    onTransitionEnd(event) {
      if (event.target === this.body && event.propertyName === 'height' && this.dataset.expanded === 'true') {
        this.body.style.height = 'auto';
      }
    }
  }

  customElements.define('db-expandable-description', DBExpandableDescription);
})();
